"use client";

import Link from "next/link";
import { useRef, useState } from "react";

import { edgeRead } from "@/lib/api/_fetch";
import type { ActiveTransit } from "@/lib/api/transits";

/**
 * "Which house is <graha> in from your Moon sign?" for one transit page.
 *
 * A pick asks the engine (`/transits/current`, through the same-origin
 * /_jyotish edge cache) for the server's IST date and that Moon sign, and shows
 * the house the engine counts plus its governed reading (transits.yaml). Every
 * label arrives as a prop, so no dataset enters the bundle; the only module
 * beyond React is edgeRead, already in the root layout's client graph. No URL
 * state, no analytics, no offer. translate="no": the result swaps in place,
 * which page translation would break.
 */

/** @internal */
export type MoonSignHousePlanet = "saturn" | "jupiter" | "rahu" | "ketu" | "venus" | "mercury";

export interface MoonSignOption {
  /** Canonical rashi id, sent to the engine. */
  id: string;
  /** "Mesha" / "मेष". */
  label: string;
  /** "Aries". */
  sub: string;
}

export interface MoonSignHouseCheckProps {
  planet: MoonSignHousePlanet;
  /** The graha's short name: "Guru" / "गुरु". */
  planetLabel: string;
  language: "en" | "hi";
  /** The render's IST date (YYYY-MM-DD); the engine is asked for this day. */
  date: string;
  signs: readonly MoonSignOption[];
}

interface HouseResult {
  house: number;
  /** The governed reading, or null where the engine has none for this house. */
  reading: string | null;
}

type CheckState =
  | { kind: "idle" }
  | { kind: "loading"; sign: string }
  | { kind: "ready"; sign: string; result: HouseResult }
  | { kind: "error"; sign: string };

const READ_TIMEOUT_MS = 10_000;

/**
 * The result panel's reserve, so no state moves the page below. Measured
 * 2026-10-07 on next dev with Playwright at 320, 360, 640 and 1280 px, using
 * the longest governed readings (Mercury and Venus in English, Venus and
 * Mercury in Hindi), the longest sign label (Vrishchika) and the 12th house:
 * the tallest state is 267 px at 320, 224 at 360 and 138 from 640 up; idle,
 * loading and error stay under 81.
 */
const PANEL_RESERVE = "min-h-[18rem] sm:min-h-[10rem]";

/**
 * For Rahu and Ketu, transits.yaml governs only some houses; for the rest the
 * engine sends a default card with effect "neutral" ("generally favorable" or
 * "requires attention") that contradicts the classical line on the page. No
 * governed Rahu or Ketu reading uses "neutral" (tests/transit-governed-readings
 * pins that), so for these two only, "neutral" marks the default card. Saturn
 * and Jupiter use "neutral" for real governed readings, which are shown.
 */
const DEFAULT_CARD_PLANETS: ReadonlySet<MoonSignHousePlanet> = new Set(["rahu", "ketu"]);

const HI_HOUSE = [
  "",
  "पहले",
  "दूसरे",
  "तीसरे",
  "चौथे",
  "पाँचवें",
  "छठे",
  "सातवें",
  "आठवें",
  "नौवें",
  "दसवें",
  "ग्यारहवें",
  "बारहवें",
] as const;

function englishOrdinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
}

function houseOrdinal(house: number, language: "en" | "hi"): string {
  return language === "hi" ? HI_HOUSE[house] : englishOrdinal(house);
}

function planetEntry(
  transits: unknown,
  planet: MoonSignHousePlanet
): Partial<ActiveTransit> | null {
  if (!Array.isArray(transits)) return null;
  const entry = transits.find(
    (item: unknown) =>
      Boolean(item) && typeof item === "object" && (item as ActiveTransit).planet_id === planet
  );
  return (entry as Partial<ActiveTransit> | undefined) ?? null;
}

function isHouse(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 12;
}

/**
 * The house and reading for `planet`, admitted only from a reply for the same
 * day and Moon sign with a house in 1..12; anything else is an error.
 * @internal
 */
export function readHouse(
  reply: unknown,
  planet: MoonSignHousePlanet,
  sign: string,
  date: string,
  language: "en" | "hi"
): HouseResult | null {
  if (!reply || typeof reply !== "object") return null;
  const answer = reply as Record<string, unknown>;
  if (answer.date !== date || answer.moon_rashi !== sign) return null;
  const entry = planetEntry(answer.active_transits, planet);
  const house = entry?.house_from_moon;
  if (!entry || !isHouse(house)) return null;
  if (DEFAULT_CARD_PLANETS.has(planet) && entry.effect === "neutral") {
    return { house, reading: null };
  }
  const reading = language === "hi" ? entry.description_hi : entry.description_en;
  return typeof reading === "string" && reading.trim() ? { house, reading } : null;
}

function signName(option: MoonSignOption | undefined, language: "en" | "hi"): string {
  if (!option) return "";
  return language === "hi" ? option.label : `${option.label} (${option.sub})`;
}

const COPY = {
  en: {
    heading: (planet: string) => `Which house is ${planet} in from your Moon sign?`,
    basis:
      "Your Moon sign (Chandra rashi) comes from your birth date, time and place; near a change of sign, the birth time decides it.",
    calculator: "Find your Moon sign",
    calculatorHref: "/rashi-calculator/",
    group: "Your Moon sign",
    idle: (planet: string) =>
      `Pick your Moon sign to see the house ${planet} occupies from it today and the classical reading for that house.`,
    loading: (planet: string, sign: string) => `Checking ${planet} from a ${sign} Moon…`,
    result: (planet: string, sign: string, house: string) =>
      `From a ${sign} Moon, ${planet} is in the ${house} house today.`,
    readingLabel: "Classical gochar reading",
    pointer: (house: string) =>
      `For this house, read the ${house} House line under House-by-House Effects below.`,
    error: (sign: string) =>
      `The engine did not answer for ${sign} just now. Pick the sign again to retry.`,
  },
  hi: {
    heading: (planet: string) => `आपकी चंद्र राशि से ${planet} किस भाव में है?`,
    basis:
      "चंद्र राशि जन्म की तारीख, समय और स्थान से निकलती है; राशि बदलने के समय के आसपास जन्म हो तो जन्म का समय ही उसे तय करता है।",
    calculator: "अपनी चंद्र राशि जानें",
    calculatorHref: "/hi/rashi-calculator/",
    group: "आपकी चंद्र राशि",
    idle: (planet: string) =>
      `अपनी चंद्र राशि चुनें: आज ${planet} उससे किस भाव में है और उस भाव का शास्त्रीय फल यहाँ दिखेगा।`,
    loading: (planet: string, sign: string) =>
      `${sign} चंद्र राशि से ${planet} का भाव देखा जा रहा है…`,
    result: (planet: string, sign: string, house: string) =>
      `चंद्र राशि ${sign} हो तो ${planet} आज ${house} भाव में है।`,
    readingLabel: "शास्त्रीय गोचर फल",
    pointer: (house: string) =>
      `इस भाव का फल पेज पर दिए भाव-अनुसार फल में ${house} भाव की पंक्ति में पढ़ें।`,
    error: (sign: string) => `${sign} के लिए इंजन से अभी उत्तर नहीं मिला। राशि दोबारा चुनकर देखें।`,
  },
} as const;

export default function MoonSignHouseCheck({
  planet,
  planetLabel,
  language,
  date,
  signs,
}: MoonSignHouseCheckProps) {
  const [state, setState] = useState<CheckState>({ kind: "idle" });
  const latest = useRef(0);
  const answers = useRef(new Map<string, HouseResult>());
  const copy = COPY[language];
  const selected = state.kind === "idle" ? null : state.sign;
  const option = signs.find((sign) => sign.id === selected);

  async function pick(sign: string) {
    const request = ++latest.current;
    const known = answers.current.get(sign);
    if (known) {
      setState({ kind: "ready", sign, result: known });
      return;
    }
    setState({ kind: "loading", sign });
    let result: HouseResult | null = null;
    try {
      const reply = await edgeRead<unknown>(
        "transits/current",
        `moon_rashi=${sign}&date=${date}`,
        READ_TIMEOUT_MS
      );
      result = readHouse(reply, planet, sign, date, language);
    } catch {
      result = null;
    }
    if (request !== latest.current) return;
    if (!result) {
      setState({ kind: "error", sign });
      return;
    }
    answers.current.set(sign, result);
    setState({ kind: "ready", sign, result });
  }

  return (
    <div
      translate="no"
      data-moon-sign-house-check={planet}
      className="mt-6 rounded-xl border border-border-default bg-surface-raised p-4 shadow-card"
    >
      <h3 className="font-serif text-body font-bold text-text-primary">
        {copy.heading(planetLabel)}
      </h3>
      <p className="mt-1 text-body-small leading-relaxed text-text-secondary">
        {copy.basis}{" "}
        <Link
          href={copy.calculatorHref}
          prefetch={false}
          className="font-semibold text-intent-brand underline-offset-2 hover:underline"
        >
          {copy.calculator}
        </Link>
      </p>
      <div
        role="group"
        aria-label={copy.group}
        className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6"
      >
        {signs.map((sign) => {
          const pressed = sign.id === selected;
          return (
            <button
              key={sign.id}
              type="button"
              aria-pressed={pressed}
              onClick={() => void pick(sign.id)}
              className={`flex flex-col items-center rounded-lg border px-2 py-2 text-center transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-intent-brand ${
                pressed
                  ? "border-intent-brand bg-surface-brand-subtle text-text-primary"
                  : "border-border-default bg-surface-warm text-text-secondary hover:bg-surface-pressed"
              }`}
            >
              <span className="text-body-small font-semibold">{sign.label}</span>
              <span className="text-caption text-text-muted">{sign.sub}</span>
            </button>
          );
        })}
      </div>
      <div
        aria-live="polite"
        data-check-state={state.kind}
        className={`mt-3 rounded-lg border border-border-subtle bg-surface-canvas p-3 ${PANEL_RESERVE}`}
      >
        {state.kind === "idle" ? (
          <p className="text-body-small text-text-secondary">{copy.idle(planetLabel)}</p>
        ) : null}
        {state.kind === "loading" ? (
          <p className="text-body-small text-text-secondary">
            {copy.loading(planetLabel, signName(option, language))}
          </p>
        ) : null}
        {state.kind === "error" ? (
          <p className="text-body-small text-text-secondary">
            {copy.error(signName(option, language))}
          </p>
        ) : null}
        {state.kind === "ready" ? (
          <>
            <p className="text-body font-semibold text-text-primary">
              {copy.result(
                planetLabel,
                signName(option, language),
                houseOrdinal(state.result.house, language)
              )}
            </p>
            {state.result.reading ? (
              <>
                <p className="mt-2 text-eyebrow font-semibold uppercase tracking-eyebrow text-text-muted">
                  {copy.readingLabel}
                </p>
                <p className="mt-1 text-body-small leading-relaxed text-text-secondary">
                  {state.result.reading}
                </p>
              </>
            ) : (
              <p className="mt-2 text-body-small leading-relaxed text-text-secondary">
                {copy.pointer(houseOrdinal(state.result.house, language))}
              </p>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
